// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : uix root actions and annotations (actions.meta creators)
// =============================================================================
// ---Third-party imports
import { createAction, handleAction as _handleAction, handleActions } from 'redux-actions';
import now from 'performance-now';
// ---Framework imports
import log from 'uix/logging';
import { iterItems, filterItems, has, isFunction, isObject, isNil, groupBy, entries, mapItems, dict, isString } from 'ldash';
// ---Globals
import { handleAction, handleSubState } from 'uix/utils/reducers';
export { createAction, handleAction, handleActions, handleSubState };
export const handleServerEvent = (eventName, reducer)=>handleAction('SERVER/EVENT', (state, action)=>action.meta.eventName === eventName ? reducer(state, action) : state);
log.info('setup core actions');
// If the payload is an instance of an Error object,
// redux-actions will automatically set action.error to true.
// do not log this action (write your own if you need control on input)
export let SILENT = (...args)=>({
        silent: true
    });
// do not log this action, ... ever
export let SECURITY = (...args)=>({
        security: true
    });
// storePaths can be a list of dotted store paths
// more state will be available via enhancedActionReducer
// XXX: you have two options to avoid using this:
// - add the required state during action creation
// - reduce the root of the store
export let MORE_STORE = (storePaths)=>(...args)=>({
            moreStorePaths: storePaths
        });
// surely unique and monotonic ascending
// divide by 10**5 to get seconds since startup
export let UNIQUE_ID = (...args)=>({
        uid: parseInt(now() * 100)
    });
// add meta.timestamp
export let TIMESTAMP = (...args)=>({
        timestamp: now()
    });
// create named integer generator
export const GENERATORS = {};
export const GENERATOR = (name)=>{
    let key = `__${name}`;
    if (!has(GENERATORS, key)) {
        GENERATORS[key] = 0;
        // use GENERATORS.myGen() for fetching values directly
        // instead of using the meta creator
        GENERATORS[name] = ()=>{
            GENERATORS[key] += 1;
            return GENERATORS[key];
        };
    }
    return (...args)=>{
        GENERATORS[key] += 1;
        //~ log.debug('generate %o %o', name, GENERATORS[name])
        return {
            [name]: GENERATORS[key]
        };
    };
};
// side effect stuff
export let resetGenerators = ()=>{
    iterItems(GENERATORS, (k, v)=>{
        if (!isFunction(v)) {
            GENERATORS[k] = 0;
        }
    });
};
// pass through copy of data
export let COPY2PAYLOAD = (data)=>({
        ...data
    });
// combine meta creators
export const combine = (...META)=>(...args)=>META.filter((e)=>isFunction(e)).map((e)=>e(...args)).reduce((merged, current)=>({
                ...merged,
                ...current
            }), {});
// filter undefined values from action input params if needed
const filterUndefined = (d)=>dict(filterItems(d, (k, v)=>v !== undefined));
export const Actions = {
    // set application state, optionally change deploy config
    setAppState: createAction('SET_APP_STATE', ({ state, deploy, router404 })=>({
            state,
            deploy,
            router404
        }), MORE_STORE([
        'router'
    ])),
    initDomain: createAction('INIT_DOMAIN', (dname, reason)=>({
            dname,
            reason
        })),
    setDomainData: createAction('SET_DOMAIN_DATA', (ddata, dcode)=>({
            ...ddata,
            dcode
        })),
    // server signals domains that are reloaded
    serverDomainsReloaded: createAction('SERVER_DOMAINS_RELOADED', COPY2PAYLOAD),
    // server signals domains are saved but not reloaded
    setDirtyConfigState: createAction('SERVER_DIRTY_CONFIGS', COPY2PAYLOAD),
    // change router params in store
    dispatchRoute: createAction('DISPATCH_ROUTE', ({ params })=>({
            params
        })),
    // build a url by merging current and given request params
    // process with saga, meta.viewId is to track context of filter calls
    go: createAction('GO', (urlOrRequest = {}, options = {})=>({
            urlOrRequest: isString(urlOrRequest) ? urlOrRequest : filterUndefined(urlOrRequest),
            options
        }), (_, options = {})=>options.runId ? {
            runId: options.runId
        } : GENERATOR('runId')()),
    // replace querystring via window.history.replaceState( {} , '', '/foo?x=1&y=1')
    // the "cut&paste" helper
    replaceQueryParams: createAction('REPLACE_QUERY_PARAMS', (params)=>({
            params
        }), SILENT),
    // set store section to initial state
    // can be used by tests over ssr
    setInitialState: createAction('SET_INITIAL_STATE', (section)=>({
            section
        })),
    // collected @vl-data-* attributes from element with @id=root from hosting html
    setDeployConfig: createAction('SET_DEPLOY_CONFIG', COPY2PAYLOAD),
    // register/unregister mounted dom nodes that are portal targets
    mountPortal: createAction('MOUNT_PORTAL', COPY2PAYLOAD, SILENT),
    // set server side session var
    setSessionVar: createAction('SET_SESSION_VAR', (name, value)=>({
            name,
            value
        })),
    restartApp: createAction('RESTART_APP', COPY2PAYLOAD),
    registerApiError: createAction('ERROR_API', COPY2PAYLOAD),
    registerServerError: createAction('ERROR_SERVER', COPY2PAYLOAD),
    registerSagaError: createAction('ERROR_SAGAS', COPY2PAYLOAD),
    setGraphicsLoaded: createAction('SET_GRAPHICS_LOADED', (name2data)=>({
            name2data
        })),
    loadGraphics: createAction('LOAD_GRAPHICS', (names)=>({
            names
        })),
    // For :callback and :reducer callbacks in callApi and reduceApi
    // https://redux.js.org/style-guide/style-guide#do-not-put-non-serializable-values-in-state-or-actions
    // we process them only with redux-saga so we are such a "Exception"
    // --
    // generic api call - it's sometimes more appropriate to use an explicit action
    // the callback should have the signature: (apiResult, apiError) => Action?
    // and can return a new action
    // you can omit either params or callback but not both
    callApi: createAction('CALL_API', (name, params, callback, options)=>({
            name,
            params: isObject(params) ? params : {},
            callback: isFunction(params) ? params : isFunction(callback) ? callback : undefined,
            options: isObject(callback) ? callback : options || {
                method: 'post'
            }
        })),
    getApi: createAction('GET_API', (name, params, key, options)=>({
            name,
            params,
            key,
            options
        }), combine(GENERATOR('apiId'), SILENT)),
    setApi: createAction('SET_API', (key, result, apiId, runId)=>({
            key,
            result,
            apiId,
            runId
        }), SILENT),
    clearApiResult: createAction('CLEAR_API_RESULT', (key)=>({
            key
        }), SILENT),
    // the generic redux version - transport a reducer function
    // the reducer should have the signature: (dataState, apiResult, apiError) => mergeIntoDataState
    // a reduceApi action is not easily testable from python-tests due to reducer function
    // some discussion: https://stackoverflow.com/q/39019022/10595423
    // you can omit params
    reduceApi: createAction('REDUCE_API', (name, params, reducer, options)=>({
            name,
            params: isObject(params) ? params : {},
            reducer: isFunction(params) ? params : reducer,
            options: options || {
                method: 'get'
            }
        })),
    // process results of reduceApi()
    reduceApiResult: createAction('REDUCE_API_RESULT', (data, error, reducer, name)=>({
            data,
            error,
            reducer,
            name
        })),
    loadChunks: createAction('LOAD_CHUNKS', (names)=>({
            names
        })),
    // if there is a valid login - send ui state to server
    saveUiState: createAction('SAVE_UI_STATE'),
    // we receive a server-stored uistate via initApp+login
    initUiState: createAction('INIT_UI_STATE', (uistate)=>({
            uistate
        })),
    // track window visibility
    setWindowVisible: createAction('SET_WINDOW_VISIBLE', (visible)=>({
            visible
        }), SILENT),
    // global vlm state communication
    setToVlmState: createAction('SET_TO_VLM_STATE', (name, value)=>({
            name,
            value
        })),
    setFromVlmState: createAction('SET_FROM_VLM_STATE', (name, value)=>({
            name,
            value
        })),
    // send {otid, message, errorStack, componentStack}
    reportErrorBoundary: createAction('REPORT_ERROR_BOUNDARY', COPY2PAYLOAD)
};
